import React, { useRef, useEffect, useCallback } from 'react';
import { Popover, Button, Space, Tag, Tooltip, Typography, Empty, theme } from 'antd';
import { AimOutlined, DeleteOutlined, ExportOutlined, CompressOutlined } from '@ant-design/icons';
import { FacilityPlan, FacilityAsset, SystemKey, RatingLabel } from '../../models/types';
import { RATING_COLORS, RATING_DESCRIPTIONS, RATING_TAG } from './facilityCalculations';

export interface PlanAsset extends FacilityAsset { systemKey: SystemKey; }

interface Props {
  plan: FacilityPlan | null;
  assets: PlanAsset[];
  placingAsset: PlanAsset | null;
  highlightAssetId: string | null;
  onPlacePin: (asset: PlanAsset, x: number, y: number) => void;
  onRemovePin: (asset: PlanAsset) => void;
  onOpenAsset: (asset: PlanAsset) => void;
  /** Pins outside the editor's current text/rating filter fade back but stay tappable. */
  isDimmed?: (asset: PlanAsset) => boolean;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const CLICK_SLOP_PX = 6;
/* The pin glyph draws at 22px. A transparent box this size is centred on it to reach the
   44pt minimum tap target without moving where the pin actually points. */
const PIN_HIT_PX = 44;

/* Pan/zoom plan canvas with fractional-coordinate pins (pattern ported from durham-om).
   View transform is applied via direct DOM style for smooth drag/pinch — React state
   is only involved for pins and mode changes. Pins counter-scale via a CSS variable
   so they stay a constant screen size at any zoom. */
const PlanViewer: React.FC<Props> = ({
  plan, assets, placingAsset, highlightAssetId, onPlacePin, onRemovePin, onOpenAsset, isDimmed,
}) => {
  const { token } = theme.useToken();
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const view = useRef({ scale: 1, tx: 0, ty: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchDist = useRef(0);
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const movedPx = useRef(0);

  const applyView = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const v = view.current;
    el.style.transform = `translate(${v.tx}px, ${v.ty}px) scale(${v.scale})`;
    el.style.setProperty('--pin-inv', String(1 / v.scale));
  }, []);

  const resetView = useCallback(() => {
    view.current = { scale: 1, tx: 0, ty: 0 };
    applyView();
  }, [applyView]);

  useEffect(() => { resetView(); }, [plan?.id, resetView]);

  const zoomAt = useCallback((clientX: number, clientY: number, factor: number) => {
    const outer = outerRef.current;
    if (!outer) return;
    const rect = outer.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const v = view.current;
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
    const ratio = next / v.scale;
    view.current = {
      scale: next,
      tx: px - (px - v.tx) * ratio,
      ty: py - (py - v.ty) * ratio,
    };
    applyView();
  }, [applyView]);

  /* Wheel must be non-passive to preventDefault page scroll. Keyed on plan?.id, not just zoomAt:
     the outer element does not exist until a plan is selected (the no-plan branch renders <Empty>),
     so the listener has to (re)attach when a plan appears — otherwise wheel/trackpad-pinch zoom is
     dead for a plan uploaded after this tab mounted empty. */
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    outer.addEventListener('wheel', onWheel, { passive: false });
    return () => outer.removeEventListener('wheel', onWheel);
  }, [zoomAt, plan?.id]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    movedPx.current = 0;
    if (pointers.current.size === 1) {
      dragStart.current = { x: e.clientX, y: e.clientY, tx: view.current.tx, ty: view.current.ty };
    } else if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinchDist.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      movedPx.current = Math.max(movedPx.current, Math.hypot(dx, dy));
      view.current.tx = dragStart.current.tx + dx;
      view.current.ty = dragStart.current.ty + dy;
      applyView();
    } else if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist.current > 0) {
        zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, dist / pinchDist.current);
      }
      pinchDist.current = dist;
      movedPx.current = CLICK_SLOP_PX + 1;
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const wasSinglePointer = pointers.current.size === 1;
    pointers.current.delete(e.pointerId);
    pinchDist.current = 0;
    if (!wasSinglePointer || movedPx.current > CLICK_SLOP_PX) return;
    // Treat as a tap: place a pin if in placement mode.
    if (placingAsset && innerRef.current) {
      const rect = innerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      if (x >= 0 && x <= 1 && y >= 0 && y <= 1) onPlacePin(placingAsset, x, y);
    }
  };

  if (!plan) {
    return (
      <Empty
        description="No floor plan yet — upload a plan image or PDF to start pinning assets."
        style={{ padding: '60px 0' }}
      />
    );
  }

  const pinned = assets.filter(a => a.pin && a.pin.planId === plan.id);

  return (
    <div
      ref={outerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={resetView}
      /* Height comes from a class so it can use dvh — see mobile.css. Inline styles
         cannot carry the vh fallback for browsers without dvh support. */
      className="plan-viewer-canvas"
      style={{
        overflow: 'hidden', position: 'relative',
        background: token.colorFillQuaternary, borderRadius: token.borderRadiusLG,
        touchAction: 'none', cursor: placingAsset ? 'crosshair' : 'grab', userSelect: 'none',
      }}
    >
      <div ref={innerRef} style={{ position: 'relative', transformOrigin: '0 0', width: '100%' }}>
        <img
          src={plan.url} alt={plan.name} draggable={false}
          style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
        />
        {pinned.map(a => {
          const color = a.rating ? RATING_COLORS[a.rating as RatingLabel] : token.colorPrimary;
          const highlighted = a.id === highlightAssetId;
          // The highlighted pin (just placed or just opened) is never dimmed.
          const dimmed = !highlighted && !!isDimmed?.(a);
          return (
            <Popover
              key={a.id}
              trigger="click"
              title={<>{a.assetId || '—'} <Typography.Text type="secondary">{a.description || 'no description'}</Typography.Text></>}
              content={
                <Space direction="vertical" size={6}>
                  {a.location && <Typography.Text type="secondary">{a.location}</Typography.Text>}
                  {a.rating && (
                    /* `title` would only show on hover, which never happens on a tablet. */
                    <Tooltip title={RATING_DESCRIPTIONS[a.rating]} trigger={['hover', 'click']}>
                      {/* RATING_TAG, not the pin's solid `color`: AntD puts white text on a
                          custom hex, and white on Poor #ffc107 is 1.6:1. The solid colour is
                          right for the pin glyph below, wrong behind a label. */}
                      <Tag color={RATING_TAG[a.rating]}>
                        {a.score !== undefined ? `${Math.round(a.score * 10) / 10} · ` : ''}{a.rating}
                      </Tag>
                    </Tooltip>
                  )}
                  <Space>
                    <Button size="small" icon={<ExportOutlined />} onClick={() => onOpenAsset(a)}>Open asset</Button>
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemovePin(a)}>Remove pin</Button>
                  </Space>
                </Space>
              }
            >
              <div
                role="button"
                aria-label={`Pin: ${a.assetId || a.description}`}
                onPointerDown={e => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: `${a.pin!.x * 100}%`, top: `${a.pin!.y * 100}%`,
                  transform: 'translate(-50%, -100%) scale(var(--pin-inv, 1))',
                  transformOrigin: 'bottom center',
                  cursor: 'pointer', zIndex: highlighted ? 3 : dimmed ? 1 : 2,
                  opacity: dimmed ? 0.25 : 1, transition: 'opacity .15s',
                }}
              >
                {/* Sits inside the counter-scaled wrapper, so it stays 44 screen px at any
                    zoom. Transparent and out of flow — the glyph below is unaffected. */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute', left: '50%', top: '50%',
                    width: PIN_HIT_PX, height: PIN_HIT_PX,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
                <AimOutlined
                  style={{
                    fontSize: highlighted ? 30 : 22,
                    color,
                    background: '#fff',
                    borderRadius: '50%',
                    padding: 3,
                    boxShadow: highlighted
                      ? `0 0 0 6px ${token.colorPrimaryBg}, 0 2px 6px rgba(0,0,0,.35)`
                      : '0 2px 6px rgba(0,0,0,.35)',
                  }}
                />
              </div>
            </Popover>
          );
        })}
      </div>
      {/* Recovering a zoomed-and-panned view used to be double-tap only, advertised in a
          caption below the canvas. stopPropagation keeps the tap from starting a pan. */}
      <Button
        size="small"
        icon={<CompressOutlined />}
        onClick={resetView}
        onPointerDown={e => e.stopPropagation()}
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 4 }}
      >
        Reset view
      </Button>
      {placingAsset && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          background: token.colorBgContainer, border: `1px solid ${token.colorPrimary}`,
          borderRadius: token.borderRadius, padding: '6px 14px', fontSize: 13, zIndex: 4,
          boxShadow: token.boxShadowSecondary,
        }}>
          Tap the plan to place the pin for <b>{placingAsset.assetId || placingAsset.description || 'asset'}</b>
        </div>
      )}
    </div>
  );
};

export default PlanViewer;
